import {Rectangle} from "pixi.js";
import {ElevationApp} from "../../ElevationApp";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {Label} from "../../shared-2d/Label";
import {drawCutMarker} from "../../shared-2d/cutHatch";
import {inches, lineBlueHeavy, lineBlueLight, panelMetalGrey} from "../../const";
import {faceSide, getProfileObj, getSwingSide, pinDirection, TProfile} from "../frameProfile/Helpers";
import {frameJambOf, ruleForItem, standardFrameY} from "../../model/hardware/MdHardwareRules";
import {HardwareItem} from "../../../../../../api/Hardware";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {Subscription} from "nate-react-api-helpers";
import {EPS} from "../../drawfrm/FrmShapePoint";

export type Jamb = "strike" | "hinge";

// the caption over the column, matching the other columns (see LocationsView).
const CAPTION_FONT = 60;
const CAPTIONS: Record<Jamb, string> = {strike: "Strike", hinge: "Hinge"};

// the prep cutouts in the strike jamb's rabbet face, centred on the latch / deadbolt
// (scene units = mm): the ANSI (ASA) strike and the deadbolt strike.
const ASA_CUTOUT = {width: 1.25 * inches, height: 4.875 * inches};
const DEADBOLT_CUTOUT = {width: 1.125 * inches, height: 2.75 * inches};
const CUTOUT_RADIUS = 0.125 * inches;
const OUTLINE = 0.2 * inches;

// the hinge mortise in the hinge jamb's rabbet: the hinge leaf runs from the barrel at the
// door's pull face to this backset off the stop.
const FRAME_HINGE_BACKSET = 5 / 16 * inches;

// the label beside the hinge jamb naming a frame placement there (its product code),
// off the stick's far edge at the placement height.
const PLACEMENT_LABEL_FONT = 40;
const PLACEMENT_LABEL_GAP = 1 * inches;

// the tick marking a jamb-mounted frame placement with no cutout at its height.
const TICK_RISE = 0.75 * inches;
const TICK_STROKE = 1.5;

// One jamb of a door as one stick, seen from the door edge: the frame height tall (the
// top of the jamb at y=0, like the other columns) and the full jamb depth wide, with a
// light line at every step of the profile seen from the door (the stop's two edges, the
// rabbet's edge).
//
// The strike jamb sits beside the door's latch edge so its preps line up with the latch
// and deadbolt faceplates: the ASA / deadbolt cutouts in the rabbet at the strike heights
// (the strike centres the cut plan uses), centred on the door thickness. The hinge jamb
// sits past the back face, as the fold-out's far side, with a mortise per hinge at the
// hinge heights.
//
// Frame hardware ruled onto this jamb (see frameJambOf) shows at its height: a cutout when
// its rule sizes one, else a tick across the face. Nothing here is edited: the heights
// follow the preps, the hinges and the frame crosshairs.
export class JambView extends ChildView {
    active: boolean;
    jamb: Jamb;

    private caption: Label;
    private captionGap: number;

    parentThing: DrawFrmThing;
    private stick: DrawFrmThing;

    // the jamb's plan section, read for the jamb depth and where the leaf sits in the
    // rabbet; not drawn.
    private profile: TProfile;
    private swing: "in" | "out";

    private placementSubs: Subscription[] = [];
    private hingeSubs: Subscription[] = [];

    // one label per frame placement on the hinge jamb, rebuilt each redraw.
    private placementLabels: Label[] = [];

    constructor(elev: ElevationApp, add: AddFx, active: boolean, jamb: Jamb, captionGap: number) {
        super(elev, add);
        this.active = active;
        this.jamb = jamb;
        this.captionGap = captionGap;

        this.dimensionMode.emit(false);

        this.caption = new Label({text: CAPTIONS[jamb], fontSize: CAPTION_FONT, color: lineBlueHeavy, anchorX: 0.5, anchorY: 1});
        this.add(this.caption);

        this.parentThing = new DrawFrmThing();
        this.add(this.parentThing);

        this.stick = new DrawFrmThing();
        this.parentThing.add(this.stick);

        this.stick.makeInteractive({
            onClick: () => {
                if (this.elev.draw.isPanSuspended()) return;
                this.elev.viewSettings.clearSelection();
            },
        });

        const relayout = () => {
            this.redraw();
            this.elev.draw.currentScene?.applyLayout();
        };
        const redraw = () => this.redraw();

        let direction: "in" | "out" | "both";
        try {
            direction = getSwingSide(elev, active).direction;
        } catch {
            direction = "in";
        }
        this.swing = pinDirection(direction);

        this.profile = getProfileObj({
            elev,
            view: this,
            side: "left",
            direction,
            detail: elev.frameSpec.doorFrameProfile,
            jambDepth: elev.frameSpec.jambDepth,
            jambFace: elev.frameSpec.jambFace,
        });
        this.trackSubscription(this.profile.changed.subscribe(() => relayout()));

        this.watchPlacements();
        this.trackSubscription(elev.hardware.listChanged.subscribe(() => {
            this.watchPlacements();
            this.redraw();
        }));

        if (jamb === "strike") {
            // the strike heights follow the preps on this leaf.
            const side = active ? "active" : "inactive";
            for (const kind of ["161", "db"] as const) {
                const anchor = elev.hardware.prepAnchors[side][kind];
                this.trackSubscription(anchor.aboveFloor.subscribe(redraw));
                this.trackSubscription(anchor.fromStrike.subscribe(redraw));
            }
            this.trackSubscription(elev.doorSpec.actualHeight.subscribe(redraw));
            this.trackSubscription(elev.doorSpec.undercut.subscribe(redraw));
        } else {
            this.watchHinges();
            this.trackSubscription(elev.hardware.hinges.listChanged.subscribe(() => {
                this.watchHinges();
                this.redraw();
            }));
        }

        this.trackSubscription(elev.doorSpec.doorThickness.subscribe(relayout));
        this.trackSubscription(elev.frameSpec.nominalHeight.subscribe(relayout));
        this.trackSubscription(elev.frameSpec.undercutFrame.subscribe(redraw));
        this.trackSubscription(elev.frameSpec.frameStopToDoorGap.subscribe(relayout));

        this.redraw();
    }

    private frontIsLocationOne(): boolean {
        return faceSide(this.elev, "front") === "one";
    }

    // the frame placements on this jamb (see frameJambOf), with the item each one is for.
    // A pair's frame placements are common to both leaves, so its hinge jamb shows them on
    // the active leaf only.
    private jambPlacements(): {placement: MdHardwarePlacement; item: HardwareItem}[] {
        if (this.jamb === "hinge" && this.elev.frameSpec.isPair && !this.active) return [];

        const hw = this.elev.hardwareGroup?.hardware ?? [];
        const out: {placement: MdHardwarePlacement; item: HardwareItem}[] = [];
        for (const p of this.elev.hardware.list) {
            if (p.target !== "frame") continue;
            const item = hw.find(h => h.product === p.productId);
            if (!item || frameJambOf(item) !== this.jamb) continue;
            out.push({placement: p, item});
        }
        return out;
    }

    // a frame placement's height: where it was put, or, untouched, its standard height
    // (the grid's crosshair seeds the same; it may not have drawn yet).
    private placementY(placement: MdHardwarePlacement, item: HardwareItem, frameHeight: number): number {
        if (placement.y.isModified()) return placement.y.get();
        return standardFrameY(this.elev, item) ?? frameHeight / 2;
    }

    // follow the jamb placements' heights (a frame crosshair is still dragged on the grid).
    private watchPlacements() {
        for (const sub of this.placementSubs) sub.cancel();
        this.placementSubs = this.jambPlacements().map(p => p.placement.y.subscribe(() => { if (!this.stick.destroyed) this.redraw(); }));
    }

    // follow the hinge heights (a hinge dimension is edited on the faces).
    private watchHinges() {
        for (const sub of this.hingeSubs) sub.cancel();
        this.hingeSubs = this.elev.hardware.hinges.list.map(h => h.offsetTop.subscribe(() => { if (!this.stick.destroyed) this.redraw(); }));
    }

    // the stick across, in parentThing's local coords: the full jamb depth (the section's
    // extent across the wall), the steps of the profile seen from the door, the centre of
    // the door thickness in the rabbet (the stop gap off the stop) and the hinge leaf's
    // span from the backset off the stop to the door's pull face. Across the stick runs the
    // section's plan y, turned as the latch edge turns the head (local x = -sy * plan y), so
    // the strike jamb has location one on the front (left) side when the front face is
    // location one. The hinge jamb sits past the back face, so it is the mirror image.
    private geometry(): {width: number; steps: number[]; doorCX: number; hingeX0: number; hingeX1: number} {
        const front = this.frontIsLocationOne() ? 1 : -1;
        const sy = this.jamb === "hinge" ? -front : front;
        const doorSideSign = this.swing === "in" ? -1 : 1;

        const lb = this.profile.graphic.getLocalBounds();
        const toX = (planY: number) => sy > 0 ? lb.y + lb.height - planY : planY - lb.y;

        const ay = this.profile.primaryDoorAnchor.getLocalY();
        const gap = this.elev.frameSpec.frameStopToDoorGap.get();
        const thickness = this.elev.doorSpec.doorThickness.get();
        const hingeEnds = [toX(ay + doorSideSign * FRAME_HINGE_BACKSET), toX(ay + doorSideSign * (gap + thickness))];
        return {
            width: lb.height,
            steps: profileSteps(this.profile).map(toX),
            doorCX: toX(ay + doorSideSign * (gap + thickness / 2)),
            hingeX0: Math.min(...hingeEnds),
            hingeX1: Math.max(...hingeEnds),
        };
    }

    redraw() {
        const geo = this.geometry();
        const fs = this.elev.frameSpec;
        const frameHeight = fs.nominalHeight.get();
        const frameUndercut = fs.undercutFrame.get();

        const g = this.stick.lines;
        g.clear();

        // the stick: the jamb seen from the door edge, the frame height tall.
        g.lineStyle(2, lineBlueHeavy, 1);
        g.beginFill(panelMetalGrey, 1);
        g.drawRect(0, 0, geo.width, frameHeight);
        g.endFill();
        if (frameUndercut > 0) drawCutMarker(g, 0, frameHeight - frameUndercut, geo.width, frameUndercut);

        // the profile's steps seen from the door: the stop's edges, the rabbet's edge.
        g.lineStyle(1, lineBlueHeavy, 0.4);
        for (const x of geo.steps) {
            g.moveTo(x, 0); g.lineTo(x, frameHeight);
        }

        const cutout = (x: number, y: number, width: number, height: number) => {
            g.lineStyle(OUTLINE, lineBlueHeavy, 1);
            g.beginFill(0xffffff, 1);
            g.drawRoundedRect(x, y, width, height, CUTOUT_RADIUS);
            g.endFill();
        };

        if (this.jamb === "strike") {
            // the prep cutouts at the latch / deadbolt heights.
            for (const strike of this.elev.hardware.strikeCenters()) {
                const size = strike.kind === "deadbolt" ? DEADBOLT_CUTOUT : ASA_CUTOUT;
                cutout(geo.doorCX - size.width / 2, strike.y - size.height / 2, size.width, size.height);
            }
        } else {
            // the hinge mortises at the hinge heights.
            const hinges = this.elev.hardware.hinges;
            hinges.list.forEach((h, i) => {
                const hh = hinges.hingeHeightAt(i);
                cutout(geo.hingeX0, h.offsetTop.get() - hh / 2, geo.hingeX1 - geo.hingeX0, hh);
            });
        }

        // frame hardware on this jamb: its cutout centred on the door thickness, else a
        // tick across the face at its height. On the hinge jamb each one is named beside
        // the stick, so the non-hinge components read apart from the hinge mortises.
        for (const l of this.placementLabels) l.destroy();
        this.placementLabels = [];
        for (const {placement, item} of this.jambPlacements()) {
            const size = ruleForItem(item)?.jambCutout;
            const cy = this.placementY(placement, item, frameHeight);
            if (size) {
                cutout(geo.doorCX - size.width / 2, cy - size.height / 2, size.width, size.height);
            } else {
                g.lineStyle(TICK_STROKE, lineBlueLight, 1);
                g.moveTo(0, cy); g.lineTo(geo.width, cy);
                const cx = geo.width / 2;
                g.moveTo(cx, cy - TICK_RISE); g.lineTo(cx, cy + TICK_RISE);
            }

            if (this.jamb === "hinge") {
                const label = new Label({text: item.productCode, fontSize: PLACEMENT_LABEL_FONT, color: lineBlueHeavy, anchorX: 0, anchorY: 0.5});
                label.graphic.position.set(geo.width + PLACEMENT_LABEL_GAP, cy);
                this.stick.graphic.addChild(label.graphic);
                this.placementLabels.push(label);
            }
        }
    }

    contentBounds(): Rectangle {
        return this.parentThing.graphic.getLocalBounds();
    }

    placeAt(x: number) {
        const b = this.contentBounds();
        this.parentThing.graphic.position.set(x, 0);
        this.caption.graphic.position.set(x + b.x + b.width / 2, b.y - this.captionGap);
    }

    destroy() {
        for (const sub of this.placementSubs) sub.cancel();
        this.placementSubs = [];
        for (const sub of this.hingeSubs) sub.cancel();
        this.hingeSubs = [];
        for (const l of this.placementLabels) if (!l.destroyed) l.destroy();
        this.placementLabels = [];
        this.profile.destroy();
        super.destroy();
    }
}

// the plan y of every step in the profile as seen from the door (looking toward -plan x):
// at each y, the surface facing the door is the segment with the greatest x covering it;
// a step is where that surface changes. Without an outline, just the rabbet anchor.
function profileSteps(profile: TProfile): number[] {
    const segments = profile.outline?.outlineSegments();
    if (!segments || segments.length === 0) return [profile.primaryDoorAnchor.getLocalY()];

    // each segment covers a y span at (up to) its greatest x; a segment along x covers no
    // span and is itself a step, which the surfaces either side of it produce.
    const spans = segments
        .map(s => ({y0: Math.min(s.y0, s.y1), y1: Math.max(s.y0, s.y1), x: Math.max(s.x0, s.x1)}))
        .filter(s => s.y1 - s.y0 > EPS);

    const breaks = Array.from(new Set(spans.flatMap(s => [s.y0, s.y1]))).sort((a, b) => a - b);
    const visibleX = (ya: number, yb: number) =>
        Math.max(-Infinity, ...spans.filter(s => s.y0 <= ya + EPS && s.y1 >= yb - EPS).map(s => s.x));

    const steps: number[] = [];
    for (let i = 1; i < breaks.length - 1; i++) {
        const before = visibleX(breaks[i - 1], breaks[i]);
        const after = visibleX(breaks[i], breaks[i + 1]);
        if (Math.abs(before - after) > EPS) steps.push(breaks[i]);
    }
    return steps;
}
